import { useEffect, useState, useCallback, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import Badge from '../../components/ui/Badge.jsx'
import Button from '../../components/ui/Button.jsx'
import Pagination from '../../components/ui/Pagination.jsx'
import { Select } from '../../components/ui/Field.jsx'
import { useFeedback } from '../../components/ui/feedback.jsx'
import api from '../../lib/api.js'

/*
 * Admin — global job execution history (GET /admin/jobs/executions, server-side
 * paginated + filterable by job). Page 1 auto-refreshes so newly launched jobs
 * appear and running ones update; an expanded running execution tails its logs.
 */
const STATUS_TONE = { success: 'success', completed: 'success', error: 'danger', failed: 'danger', running: 'info', pending: 'neutral', cancelled: 'neutral' }
const REFRESH_MS = 6000
const PER_PAGE = 20

export default function JobHistoryTab() {
  const { t } = useTranslation()
  const { toast } = useFeedback()
  const [jobs, setJobs] = useState([]) // for the filter dropdown
  const [jobFilter, setJobFilter] = useState('') // job_id or '' = all
  const [page, setPage] = useState(1)
  const [execs, setExecs] = useState(null)
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 })
  const [expanded, setExpanded] = useState(null) // { id, logs }
  const [retrying, setRetrying] = useState(null)
  const expandedRef = useRef(null)
  expandedRef.current = expanded
  const logRef = useRef(null)

  // tail -f: keep the log viewer pinned to the bottom as new lines arrive.
  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight
  }, [expanded?.logs])

  // Job list for the dropdown (once).
  useEffect(() => {
    api.get('/admin/jobs').then(({ data }) => setJobs((data?.jobs || []).slice().sort((a, b) => a.name.localeCompare(b.name)))).catch(() => {})
  }, [])

  const load = useCallback(async () => {
    try {
      const params = { page, per_page: PER_PAGE }
      if (jobFilter) params.job_id = jobFilter
      const { data } = await api.get('/admin/jobs/executions', { params })
      setExecs(data?.executions || [])
      setPagination(data?.pagination || { page: 1, pages: 1, total: 0 })
      // Tail: if the expanded execution is still running, refresh its logs.
      const ex = expandedRef.current
      if (ex && (data?.executions || []).find((m) => m.id === ex.id)?.status === 'running') {
        api.get(`/admin/jobs/executions/${ex.id}/logs`).then(({ data: d }) => setExpanded({ id: ex.id, logs: formatLogs(d, t) })).catch(() => {})
      }
    } catch { setExecs([]) }
  }, [page, jobFilter, t])

  useEffect(() => { load() }, [load])

  // Auto-refresh only on page 1 (don't fight the user browsing older pages).
  useEffect(() => {
    if (page !== 1) return undefined
    const id = setInterval(load, REFRESH_MS)
    return () => clearInterval(id)
  }, [load, page])

  const toggleLogs = async (ex) => {
    if (expanded?.id === ex.id) { setExpanded(null); return }
    setExpanded({ id: ex.id, logs: null })
    const { data } = await api.get(`/admin/jobs/executions/${ex.id}/logs`).catch(() => ({ data: {} }))
    setExpanded({ id: ex.id, logs: formatLogs(data, t) })
  }
  const retry = async (ex) => {
    setRetrying(ex.id)
    try { await api.post(`/admin/jobs/executions/${ex.id}/retry`); toast(t('admin.jobs.launched'), 'success'); setTimeout(load, 600) }
    catch { toast(t('admin.errorGeneric'), 'error') } finally { setRetrying(null) }
  }

  const running = (execs || []).filter((e) => e.status === 'running').length

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
        <Select value={jobFilter} onChange={(e) => { setJobFilter(e.target.value); setPage(1) }} style={{ width: 'auto', minWidth: 200, height: 40 }}>
          <option value="">{t('admin.jobs.allJobs')}</option>
          {jobs.map((j) => <option key={j.id} value={j.id}>{j.name}</option>)}
        </Select>
        {page === 1 && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--color-text-muted)' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--color-success)' }} />{t('admin.jobs.autoRefresh')}
          </span>
        )}
        {running > 0 && <Badge tone="info">{t('admin.jobs.runningCount', { count: running })}</Badge>}
        <div style={{ flex: 1 }} />
        {pagination.total > 0 && <span style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{t('admin.jobs.total', { count: pagination.total })}</span>}
        <Button variant="secondary" icon="settings" onClick={load}>{t('admin.jobs.refresh')}</Button>
      </div>

      {execs == null ? <div style={{ color: 'var(--color-text-muted)', padding: 16 }}>{t('common.loading')}</div>
        : execs.length === 0 ? <div style={{ color: 'var(--color-text-muted)', padding: 30, textAlign: 'center' }}>{t('admin.jobs.noExecutions')}</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {execs.map((ex) => (
              <div key={ex.id} style={{ border: '1px solid var(--color-border)', borderRadius: 12, padding: '12px 16px', background: ex.status === 'running' ? 'var(--color-bg-lilac)' : '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <Badge tone={STATUS_TONE[ex.status] || 'neutral'}>{ex.status}{ex.status === 'running' ? '…' : ''}</Badge>
                  <span style={{ fontWeight: 600, color: 'var(--color-ink)', fontSize: 14 }}>{ex.job_name}</span>
                  <span style={{ fontSize: 13, color: 'var(--color-text-muted)' }}>{fmtTs(ex.started_at || ex.created_at)}</span>
                  {ex.duration != null && <span style={{ fontSize: 12.5, color: 'var(--color-text-muted)' }}>{Number(ex.duration).toFixed(2)}s</span>}
                  <div style={{ flex: 1 }} />
                  <Button variant="secondary" onClick={() => toggleLogs(ex)}>{expanded?.id === ex.id ? t('admin.jobs.hideLogs') : t('admin.jobs.logs')}</Button>
                  {(ex.status === 'failed' || ex.status === 'error') && <Button variant="secondary" icon="send" loading={retrying === ex.id} onClick={() => retry(ex)}>{t('admin.jobs.retry')}</Button>}
                </div>
                {ex.error && <div style={{ marginTop: 8, fontSize: 12.5, color: 'var(--color-danger)', fontFamily: 'monospace', wordBreak: 'break-word' }}>{ex.error}</div>}
                {expanded?.id === ex.id && (
                  <pre ref={logRef} style={{ marginTop: 8, padding: 12, background: 'var(--color-ink)', color: '#e6e1f2', borderRadius: 8, fontSize: 12, overflowX: 'auto', overflowY: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 280 }}>
                    {expanded.logs == null ? `${t('common.loading')}…` : expanded.logs}
                    {ex.status === 'running' && <span style={{ opacity: 0.6 }}>{'\n'}▌ {t('admin.jobs.tailing')}</span>}
                  </pre>
                )}
              </div>
            ))}
          </div>
        )}

      {pagination.pages > 1 && (
        <Pagination page={pagination.page} totalPages={pagination.pages} total={pagination.total} unit={t('admin.jobs.unit')}
          onPage={(p) => { setPage(p); window.scrollTo({ top: 0, behavior: 'smooth' }) }} />
      )}
    </>
  )
}

function formatLogs(data, t) {
  const raw = data?.logs ?? data?.output ?? data
  if (typeof raw === 'string') return raw || t('admin.jobs.noLogs')
  if (Array.isArray(raw)) {
    if (raw.length === 0) return t('admin.jobs.noLogs')
    return raw.map((l) => {
      if (typeof l === 'string') return l
      const ts = l.timestamp ? `[${l.timestamp}] ` : ''
      const lvl = l.level ? `${String(l.level).toUpperCase()}: ` : ''
      return `${ts}${lvl}${l.message ?? JSON.stringify(l)}`
    }).join('\n')
  }
  return JSON.stringify(raw, null, 2)
}

function fmtTs(ts) {
  if (!ts) return '—'
  const d = new Date(ts)
  return `${d.toLocaleDateString('fr-BE')} ${d.toLocaleTimeString('fr-BE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
}
